<template>
  <DashboardPageLayout
    title="Returns"
    :is-loading="isLoading"
    @change="onDateRangeChange"
  >
    <template v-if="showSupervisorFilter" #filters>
      <b-form-group
        label="Supervisor"
        label-for="dashboard-returns-supervisor"
      >
        <ImageTextSelect
          id="dashboard-returns-supervisor"
          :model-value="filterSupervisorId"
          :options="supervisorFilterOptions"
          :fallback-image="supervisorPictureFallback"
          @update:model-value="onSupervisorFilterChange"
        />
      </b-form-group>
    </template>

    <template #summary>
      <div class="text-muted small">
        {{ formattedDateRange }}
      </div>
      <div
        v-if="ordersVsReturnsByDay"
        class="font-weight-bold d-flex flex-wrap align-items-center"
        style="gap: 1rem;"
      >
        <span
          class="text-nowrap"
          :style="{ color: ordersColor }"
        >
          Total orders: {{ $formatNumber(ordersVsReturnsByDay.total_orders, 0) }}
        </span>
        <span
          class="text-nowrap"
          :style="{ color: returnsColor }"
        >
          Total returns: {{ $formatNumber(ordersVsReturnsByDay.total_returns, 0) }}
        </span>
      </div>
    </template>

    <template #default="{ isLoading: chartLoading }">
      <DashboardChartCard
        type="line"
        :is-loading="chartLoading"
        :has-data="chartHasData"
        :options="chartOptions"
        :series="chartSeries"
      />
    </template>
  </DashboardPageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BFormGroup } from 'bootstrap-vue'
import DashboardPageLayout from '@/views/Components/Elements/DashboardPageLayout.vue'
import DashboardChartCard from '@/views/Components/Elements/DashboardChartCard.vue'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'
import dashboardPageMixin from '@/mixins/dashboardPageMixin'
import {
  buildDashboardChartOptions,
  buildDashboardChartViewFromStatistics,
} from '@/libs/dashboardChart'

const ORDERS_COLOR = '#0d6efd'
const RETURNS_COLOR = '#ea5455'

export default {
  components: {
    BFormGroup,
    DashboardPageLayout,
    DashboardChartCard,
    ImageTextSelect,
  },
  mixins: [dashboardPageMixin],
  data() {
    return {
      dashboardLoadErrorMessage: 'Can not load returns statistics',
      ordersColor: ORDERS_COLOR,
      returnsColor: RETURNS_COLOR,
    }
  },
  computed: {
    ...mapGetters({
      ordersVsReturnsByDay: 'dashboard/ordersVsReturnsByDay',
    }),
    ordersLabel() {
      return this.companyPurpose !== 'rent' ? 'Orders' : 'Bookings'
    },
    returnsLabel() {
      return 'Returns'
    },
    chartHasData() {
      return (this.ordersVsReturnsByDay?.days || []).length > 0
    },
    chartView() {
      return buildDashboardChartViewFromStatistics({
        statistics: this.ordersVsReturnsByDay,
        selectedDateRange: this.selectedDateRange,
        series: [
          { name: this.ordersLabel, key: 'orders_count' },
          { name: this.returnsLabel, key: 'returns_count' },
        ],
      })
    },
    chartSeries() {
      return this.chartView?.series || []
    },
    chartOptions() {
      return buildDashboardChartOptions({
        chartView: this.chartView,
        chartType: 'line',
        colors: [this.ordersColor, this.returnsColor],
        yAxisTitle: 'Quantity',
        yAxisFormatter: (value) => Math.round(value).toLocaleString(this.$getNumberLocale()),
        sharedTooltip: true,
      })
    },
  },
  methods: {
    ...mapActions({
      loadOrdersVsReturnsByDay: 'dashboard/loadOrdersVsReturnsByDay',
    }),
    fetchDashboardData(payload) {
      return this.loadOrdersVsReturnsByDay(payload)
    },
  },
}
</script>
